/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  ApprovalIcon,
  AttachmentAltIcon,
  ChevronDownIcon,
  CloseIcon,
  CommentAltIcon,
  ExpandArrowIcon,
  EyeAltIcon,
  EyeIcon,
  FileIcon,
  FolderAltIcon,
  HashIcon,
  LeftArrowIcon,
  RightArrowIcon,
  ShareAltIcon,
  ShrinkArrowIcon,
  TaskHistoryTimerAltIcon,
} from '@sketchnote/icons';
import { CheckboxMarkComplete, LoaderSm, LoaderXs } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { TElement } from '@udecode/plate';
import classNames from 'classnames';
import cn from 'classnames';
import { isNil } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import { API } from 'src/api';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { useKanbanStore } from 'src/features/kanban';
import { useTableStore } from 'src/features/list/store';
import { useAgileStore } from 'src/features/scrum/store';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';
import useSWR from 'swr';

import { options } from '../../scrum/components/task/TypeCell';
import { useTasksStore } from '../store/useTasksStore';
import { Task, TaskT } from '../types/tasks';
import { TaskTemplateImport } from './ImportTemplate';
import { EditItemLabel } from './Label';
import { TaskTemplateSave } from './SaveTaskTemplate';
const { useState, useMemo } = React;

export const EditItemHeader = ({
  onTaskViewClose,
  task,
  onCommentsClick,
  onAttachmentsClick,
  onHistoryClick,
  onShareClick,
  onApprovalsClick,
  sidebarMode,
}: {
  onTaskViewClose: () => void;
  task: TaskT;
  onDeleteTask?: (taskId: string) => void;
  onDuplicateTask?: () => void;
  onMove?: () => void;
  onExploreClick?: () => void;
  onCommentsClick?: () => void;
  onApprovalsClick?: () => void;
  onAttachmentsClick?: () => void;
  onHistoryClick?: () => void;
  onShareClick?: () => void;
  sidebarMode?: '' | 'comments' | 'attachments' | 'history' | 'share' | 'approvals';
}) => {
  const router = useRouter();
  const isFullScreen = useTasksStore((s) => s.isFullScreen);
  const [saveTemplateModal, setSaveTemplateModal] = useState(false);
  const [importTemplate, setImportTemplate] = useState(false);
  const boardsStore = useBoardStore();

  const authData = useAuth((s) => s.userDetails);
  const [open] = useSnackbar();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const { data: guestList } = useSWR('/user/guests', API.get);
  const nextTaskLink = useMemo(() => {
    return isFullScreen
      ? router.query.routeBack === 'default'
        ? `/${mode}/${orgId}/files/${task.fileId}`
        : `/${mode}/${orgId}/files/${task.fileId}/?view=${task.board?.id}&type=${router.query.routeBack}&taskId=${task?.id}`
      : `/${mode}/${orgId}/tasks/${task?.id}?type=${router.query.type}`;
  }, [isFullScreen, task?.id, router.query]);

  const [changingParent, setChangingParent] = useState(false);
  const [changingParentLoading, setChangingParentLoading] = useState(false);
  const orgMembers = useMemo(() => getAllOrgMembers(), [orgId]);
  const fileType = router.query.type as string;

  const { isReadOnly } = useTaskAccess(task?.id || '');

  const { epics, backlogTasks } = useAgileStore((s) => ({
    epics: s.epics,
    backlogTasks: s.backlogTasks,
  }));
  const columns = useKanbanStore((s) => s.columns);
  const tableColumns = useTableStore((s) => s.columns);
  const changeSubtaskParent = useTasksStore((s) => s.changeSubtaskParent);

  const parentTaskOptions = useMemo(() => {
    if (fileType === 'agile') {
      return [...epics, ...backlogTasks].filter((t) => t._id !== task?.parent?._id);
    }
    if (fileType === 'kanban') {
      const tasks: any[] = [];
      columns.forEach((column) => {
        column.items.forEach((task) => {
          tasks.push(task);
        });
      });
      return tasks.filter((t) => t._id !== task?.parent?._id);
    }
    if (fileType === 'list') {
      const tasks: any[] = [];
      tableColumns.forEach((column) => {
        column.items.forEach((task) => {
          tasks.push(task);
        });
      });
      return tasks.filter((t) => t._id !== task?.parent?._id);
    }
    return [];
  }, [epics, backlogTasks, columns, fileType, task?.parent?._id]);

  const handleParentChange = async (newParentTask) => {
    setChangingParentLoading(true);
    const res = await changeSubtaskParent(task, newParentTask);
    if (res)
      open({
        text: `Parent task changed successfully to ${getFormattedValue(newParentTask.name)}`,
        duration: 2000,
        position: 'top-center',
        snackType: 'standard',
      });
    else
      open({
        text: 'Something went wrong',
        duration: 2000,
        position: 'top-center',
        snackType: 'error',
      });
    setChangingParentLoading(false);
  };

  const onMarkAsComplete = useTasksStore((s) => s.onMarkAsComplete);

  const handleMarkAsComplete = async (e) => {
    if (task?.id) {
      const success = await onMarkAsComplete({
        taskId: task.id!,
        completed: e,
      });
      if (!success) {
        open({
          text: 'Something went wrong',
          duration: 2000,
          position: 'top-center',
          snackType: 'error',
        });
      }
    }
  };

  const OnClickBackArrow = () => {
    let pathname = `/${mode}/${orgId}/files/${task.fileId}`;
    if (boardsStore.isPublicView) pathname = pathname + '/public';
    const query = {
      view: task.board?.id,
      type: router.query.type,
      taskId: task.parent?._id,
    };
    if (isFullScreen) {
      query['routeBack'] = router.query.type || router.query.routeBack;
    } else {
      query['viewId'] = router.query.viewId;
    }
    router.push({
      pathname,
      query,
    });
  };
  const plan = authData?.plan?.type || '';
  const isAgile = router?.query?.type === 'agile';

  const taskIsInBacklog = useMemo(() => {
    return isAgile && backlogTasks.find((t) => t._id === task.id);
  }, [task]);

  const headerContent =
    task && boardsStore.isPublicView ? (
      <div className="flex items-center justify-between py-20 px-40 shadow-sm">
        <div></div>
        <button
          aria-label="Close button"
          onClick={onTaskViewClose}
          className={cn({
            'text-gray-500 text-xs hover:bg-gray-100 px-10 py-5 rounded': true,
          })}
        >
          <CloseIcon />
        </button>
      </div>
    ) : (
      <div className="flex items-center justify-between py-20 px-40 shadow-sm"></div>
    );
  return (
    <div>
      {task && !boardsStore.isPublicView ? (
        <div className="flex items-center py-12 px-32 bg-gray-100 border-b rounded-t-lg">
          <div className="flex items-center gap-12">
            {task.isSubtask && (
              <Tippy content="Back">
                <button onClick={OnClickBackArrow} className="cursor-pointer flex items-center">
                  <LeftArrowIcon wrapperClass="text-gray-500" size={16} />
                </button>
              </Tippy>
            )}
            {!taskIsInBacklog && (
              <Tippy
                content="You do not have the required permission to edit tasks"
                disabled={!isReadOnly}
              >
                <span className="mr-8">
                  <CheckboxMarkComplete
                    checked={task?.completed}
                    isDisabled={isReadOnly}
                    color="#059669"
                    onChange={handleMarkAsComplete}
                  />
                </span>
              </Tippy>
            )}
          </div>

          <div className="flex items-center gap-12 w-full">
            <div className="flex text-black items-center">
              <label className="text-sm font-regular flex items-center">
                {boardsStore.key}
                <HashIcon />
                {!isNil(task.number) && task.number > 9 ? '' : '0'}
                {task.number}
              </label>
            </div>
            <div className="flex  items-center gap-8 flex-grow min-w-0">
              {task.isSubtask && (
                <>
                  <FileIcon fill="black" />
                  <span className="text-gray-500">Parent Task: </span>
                  <Breadcrumb>
                    <BreadcrumbItem>
                      <div className="flex items-center gap-8">
                        <BreadcrumbItem>
                          <Link
                            href={
                              //public
                              boardsStore.isPublicView
                                ? `/${mode}/${orgId}/files/${task.fileId}/public?view=${task.board?.id}&type=${router.query.type}&taskId=${task.parent?._id}`
                                : isFullScreen
                                ? `/${mode}/${orgId}/tasks/${task.parent?._id}?routeBack=${
                                    router.query.type || router.query.routeBack
                                  }`
                                : `/${mode}/${orgId}/files/${task.fileId}?view=${task.parent?.board}&type=${router.query.type}&taskId=${task.parent?._id}`
                            }
                          >
                            <BreadcrumbLink> {getFormattedValue(task.parent?.name)}</BreadcrumbLink>
                          </Link>
                        </BreadcrumbItem>
                        <Menu
                          isOpen={!changingParentLoading && changingParent}
                          onClose={() => setChangingParent(false)}
                        >
                          <MenuButton
                            className="flex items-center"
                            onClick={() => setChangingParent(true)}
                          >
                            {changingParentLoading ? (
                              <LoaderXs style={{ width: 12, height: 12, borderWidth: 2 }} />
                            ) : (
                              <ChevronDownIcon />
                            )}
                          </MenuButton>
                          <Portal>
                            <div className="more-menu-popover">
                              <MenuList className="max-h-400 overflow-y-auto">
                                <div
                                  className="bg-white p-6 flex flex-col gap-0 justify-center min-w-200"
                                  style={{
                                    boxShadow: ' 0px 2px 12px rgba(19, 23, 32, 0.08)',
                                    borderRadius: '8px',
                                  }}
                                >
                                  {parentTaskOptions.map((taskOption) => {
                                    const taskType = options.find(
                                      (op) => op.type === taskOption.type
                                    );
                                    return (
                                      <MenuItem
                                        onClick={handleParentChange.bind(null, taskOption)}
                                        key={taskOption.id}
                                        className="flex px-10 h-40 items-center "
                                      >
                                        {taskType && (
                                          <span className="mr-8 block">{taskType.icon}</span>
                                        )}
                                        <span className="text-sm block  font-medium">
                                          {getFormattedValue(taskOption.name)}
                                        </span>
                                      </MenuItem>
                                    );
                                  })}
                                  {!parentTaskOptions.length && (
                                    <>
                                      <MenuItem className="flex px-10 h-40 items-center ">
                                        <span className="text-sm block  font-medium">
                                          No parent task option available
                                        </span>
                                      </MenuItem>
                                    </>
                                  )}
                                </div>
                              </MenuList>
                            </div>
                          </Portal>
                        </Menu>
                      </div>
                    </BreadcrumbItem>
                  </Breadcrumb>
                </>
              )}
              <FolderAltIcon fill="black" />
              <span className="text-gray-500">File: </span>
              <div className="min-w-0 flex-grow truncate">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link
                      href={
                        boardsStore.isPublicView
                          ? `/${mode}/${orgId}/files/${task.fileId}/public?view=${task.board?.id}&type=${router.query.type}`
                          : router.query.type
                          ? `/${mode}/${orgId}/files/${task.fileId}?view=${task.board?.id}&type=${router.query.type}`
                          : `/${mode}/${orgId}/files/${task.fileId}`
                      }
                    >
                      <BreadcrumbLink className="min-w-0">
                        {task.filename || 'Untitled'}
                      </BreadcrumbLink>
                    </Link>
                  </BreadcrumbItem>
                </Breadcrumb>
              </div>
            </div>
            <div className="flex items-center gap-4 text-gray-500 text">
              {!isReadOnly && (
                <Tippy content="Approvals">
                  <span
                    className={classNames(
                      'p-4 cursor-pointer flex items-center rounded-md',
                      sidebarMode === 'approvals' && 'bg-black text-white'
                    )}
                    onClick={onApprovalsClick}
                  >
                    <ApprovalIcon size={16} />
                  </span>
                </Tippy>
              )}
              <Tippy content="Comments">
                <span
                  className={classNames(
                    'p-4 cursor-pointer flex items-center rounded-md',
                    sidebarMode === 'comments' && 'bg-black text-white'
                  )}
                  onClick={onCommentsClick}
                >
                  <CommentAltIcon size={16} />
                </span>
              </Tippy>
              <Tippy content="Attachments">
                <span
                  className={classNames(
                    'p-4 cursor-pointer flex items-center rounded-md',
                    sidebarMode === 'attachments' && 'bg-black text-white'
                  )}
                  onClick={onAttachmentsClick}
                >
                  <AttachmentAltIcon size={16} />
                </span>
              </Tippy>
              <Tippy content="History">
                <span
                  className={classNames(
                    'p-4 cursor-pointer flex items-center rounded-md',
                    sidebarMode === 'history' && 'bg-black text-white'
                  )}
                  onClick={onHistoryClick}
                >
                  <TaskHistoryTimerAltIcon size={16} />
                </span>
              </Tippy>
              <Tippy content="Watch task">
                <span>
                  <WatchTask task={task} />
                </span>
              </Tippy>
              <Tippy content="Share">
                <span
                  className={classNames(
                    'p-4 cursor-pointer flex items-center rounded-md',
                    sidebarMode === 'share' && 'bg-black text-white'
                  )}
                  onClick={onShareClick}
                >
                  <ShareAltIcon size={16} />
                </span>
              </Tippy>

              <Tippy content={!isFullScreen ? 'Full screen' : 'View in file'}>
                <a href={nextTaskLink} className="p-4 cursor-pointer flex items-center">
                  {!isFullScreen ? (
                    <ExpandArrowIcon size={16} className="p-2" />
                  ) : (
                    <ShrinkArrowIcon size={16} className="p-2" />
                  )}
                </a>
              </Tippy>
              <div className="h-12 border-r mx-8" />
              <Tippy content="Close">
                <span className="p-4 cursor-pointer flex items-center">
                  <CloseIcon size={16} className="p-2" onClick={onTaskViewClose} />
                </span>
              </Tippy>
            </div>
          </div>

          {importTemplate && (
            <TaskTemplateImport
              task={task}
              importTemplate={importTemplate}
              setImportTemplate={setImportTemplate}
            />
          )}
          {saveTemplateModal && (
            <TaskTemplateSave
              task={task}
              saveTemplateModal={saveTemplateModal}
              setSaveTemplateModal={setSaveTemplateModal}
            />
          )}
        </div>
      ) : (
        headerContent
      )}
    </div>
  );
};

function getInitialValue(value): any[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return [{ children: [{ text: value }] }];
  }
  return [{ children: [{ text: '' }] }];
}
export const EditItemHeaderTitle = ({
  task,
}: {
  task: Partial<Task> & { isLoading?: boolean };
}) => {
  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);
  const boardsStore = useBoardStore();
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const [name, setName] = useState(task?.name);
  const [open] = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [slateKey, setSlateKey] = useState(1);

  const editor = React.useMemo(() => withHistory(withReact(createEditor() as any)) as any, []);

  const handleChangeTopic = (e: TElement[]) => {
    setName(e);
  };

  React.useEffect(() => {
    setName(task?.name);
    setSlateKey((prev) => prev + 1);
  }, [task?.name]);

  const handleBlurTopic = () => {
    if (task) {
      setSaving(true);
      onUpdateItemField({
        item: { name: name, id: task.id! },
        keys: ['name'],
      })
        .then((success) => {
          if (success) boardsStore.actions.fetchTimer();
          if (!success) {
            open({
              text: 'Something went wrong',
              duration: 2000,
              position: 'top-center',
              snackType: 'error',
            });
          }
        })
        .finally(() => setSaving(false));
    }
  };

  const { isReadOnly } = useTaskAccess(task?.id || '');

  return (
    <div className="mt-20">
      {task && (
        <>
          <div className="flex items-center mb-16">
            <div className="flex-grow">
              <EditItemLabel />
            </div>
          </div>
          <Slate
            key={slateKey}
            editor={editor}
            value={getInitialValue(name)}
            onChange={handleChangeTopic}
          >
            <Editable
              readOnly={isReadOnly || saving}
              placeholder="Title"
              id={task.id}
              onBlur={handleBlurTopic}
              className="font-bold mb-20 text-2xl w-full rounded border-2 border-transparent focus:border-gray-100 focus:bg-gray-100 focus:px-5"
            />
          </Slate>
          {task.type === 'creation' && task.creation ? (
            <div className="mb-40">
              <Link
                href={`/${mode}/${orgId}/creations/${task?.creation?.slug}?id=${task?.creation?.creationId}`}
              >
                <a className="text-blue-500 text-sm font-medium  mb-24 block">
                  <span>Generate contract</span>
                  <RightArrowIcon className="text-sm text-blue-500 ml-8 pt-4" />
                </a>
              </Link>

              <h6 className="mb-22">In Progress</h6>

              {task.creation?.tasks?.total > 0 && (
                <div>
                  <ProgressBar
                    now={calculatePercentage(
                      task.creation?.tasks?.total,
                      task.creation?.tasks?.completed
                    )}
                    variant="success"
                    className="board-item-progress mb-4"
                  />
                  <span className="text-xs text-gray-400">
                    {calculatePercentage(
                      task.creation?.tasks?.total,
                      task.creation?.tasks?.completed
                    )}
                    % &nbsp;complete
                  </span>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export const WatchTask = ({ task }: { task: TaskT }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const toggleWatchTask = useTasksStore((s) => s.toggleWatchTask);
  const [open] = useSnackbar();

  return (
    <Popover placement="bottom" onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)}>
      <PopoverTrigger>
        <button
          className={classNames(
            'p-4 cursor-pointer flex items-center rounded-md',
            isOpen && 'bg-black text-white'
          )}
        >
          <EyeAltIcon size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        zIndex={4000}
        className="border border-gray-200 bg-white shadow-lg rounded-md max-h-304 w-250 overflow-auto"
      >
        <div className="p-20">
          <button
            className="flex items-center text-primary-500 rounded p-5 hover:bg-primary-100"
            disabled={isLoading}
            onClick={async () => {
              setIsLoading(true);
              const success = await toggleWatchTask({
                taskId: task.id || '',
                watching: !task.watch,
              });
              if (!success) {
                open({
                  text: 'Something went wrong',
                  duration: 3000,
                  position: 'top-center',
                  snackType: 'error',
                });
              }
              setIsLoading(false);
            }}
          >
            <EyeIcon className="mr-6" />
            <span>{task?.watch ? 'Stop Watching' : 'Start watching'}</span>
            {isLoading && <LoaderSm className="ml-4" style={{ fontSize: '0.3rem' }} />}
          </button>
        </div>
        {task.watching && task.watching?.length > 0 ? (
          <>
            <p className="px-20 py-10 bg-gray-100 text-sm text-gray-600">
              Users watching this issue
            </p>
            {task.watching.map((user) => (
              <div className="flex items-center mx-20 my-12" key={user.memberId}>
                <img
                  height="20px"
                  width="20px"
                  className="mr-8 h-20 w-20 rounded-lg"
                  src={user.avatar}
                  loading="lazy"
                />
                <span className="text-sm">{user.firstname}</span>
              </div>
            ))}
          </>
        ) : (
          <div className="p-20 border-b border-t border-gray-100">
            <img
              height="192px"
              width="144px"
              className="mx-auto mb-8 w-full h-full"
              loading="lazy"
              src="https://res.cloudinary.com/samvibes/image/upload/v1640603413/empty%20states/Creation_Start.png"
            />
            <p className="text-sm text-center">No one is watching this task</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const calculatePercentage = (total: number, value: number) =>
  parseFloat(((value / total) * 100).toFixed());
